/**
 * Typed window events for builder session sync across chat, flow-builder, and agents.
 * Prefer these helpers over raw CustomEvent string names.
 */

export const BuilderSessionEvents = {
  draftsChanged: "flow-drafts-changed",
  sessionUrl: "flow-builder-session-url",
  chatLoaded: "flow-builder-chat-loaded",
  draftLoaded: "flow-builder-draft-loaded",
  chatsChanged: "flow-builder-chats-changed",
  openWorkflowCanvas: "flow-builder-open-canvas",
  workflowCanvasVisible: "flow-builder-canvas-visible",
  workspaceTitle: "flow-builder-workspace-title",
  validationStarted: "flow-builder-validation-started",
} as const;

export type BuilderSessionEventName =
  (typeof BuilderSessionEvents)[keyof typeof BuilderSessionEvents];

type SessionIdDetail = { sessionId?: string };
type DraftLoadedDetail = { sessionId?: string; draftId?: string };
type WorkspaceTitleDetail = { sessionId: string; title: string };
type WorkflowCanvasVisibleDetail = { open: boolean };

function canUseWindow() {
  return typeof window !== "undefined";
}

function emit<T>(name: string, detail?: T) {
  if (!canUseWindow()) return;
  window.dispatchEvent(new CustomEvent(name, detail === undefined ? undefined : { detail }));
}

function subscribe<T>(name: string, handler: (detail: T) => void): () => void {
  if (!canUseWindow()) return () => undefined;
  const listener = (event: Event) => {
    handler((event as CustomEvent<T>).detail);
  };
  window.addEventListener(name, listener);
  return () => window.removeEventListener(name, listener);
}

/** Notify home/sidebar draft lists after save or deploy. */
export function emitDraftsChanged() {
  emit(BuilderSessionEvents.draftsChanged);
}

export function onDraftsChanged(handler: () => void) {
  return subscribe(BuilderSessionEvents.draftsChanged, () => handler());
}

export function emitSessionUrl(sessionId: string) {
  emit<SessionIdDetail>(BuilderSessionEvents.sessionUrl, { sessionId });
}

export function onSessionUrl(handler: (detail: SessionIdDetail) => void) {
  return subscribe<SessionIdDetail>(BuilderSessionEvents.sessionUrl, handler);
}

/** Chat finished loading a session into the builder workspace. */
export function emitChatLoaded(detail: SessionIdDetail = {}) {
  emit<SessionIdDetail>(BuilderSessionEvents.chatLoaded, detail);
}

export function onChatLoaded(handler: (detail: SessionIdDetail) => void) {
  return subscribe<SessionIdDetail>(BuilderSessionEvents.chatLoaded, handler);
}

export function emitDraftLoaded(detail: DraftLoadedDetail = {}) {
  emit<DraftLoadedDetail>(BuilderSessionEvents.draftLoaded, detail);
}

export function onDraftLoaded(handler: (detail: DraftLoadedDetail) => void) {
  return subscribe<DraftLoadedDetail>(BuilderSessionEvents.draftLoaded, handler);
}

export function emitChatsChanged() {
  emit(BuilderSessionEvents.chatsChanged);
}

export function onChatsChanged(handler: () => void) {
  return subscribe(BuilderSessionEvents.chatsChanged, () => handler());
}

/** Open the workflow canvas (e.g. after Build workflow / sidebar jump). */
export function emitOpenWorkflowCanvas() {
  emit(BuilderSessionEvents.openWorkflowCanvas);
}

export function onOpenWorkflowCanvas(handler: () => void) {
  return subscribe(BuilderSessionEvents.openWorkflowCanvas, () => handler());
}

/** App sidebar collapses only while the workflow canvas is open. */
export function emitWorkflowCanvasVisible(open: boolean) {
  emit<WorkflowCanvasVisibleDetail>(BuilderSessionEvents.workflowCanvasVisible, { open });
}

export function onWorkflowCanvasVisible(handler: (open: boolean) => void) {
  return subscribe<WorkflowCanvasVisibleDetail>(
    BuilderSessionEvents.workflowCanvasVisible,
    (detail) => handler(Boolean(detail?.open)),
  );
}

/** Sidebar rename of the open chat — keep the workspace header name in sync. */
export function emitWorkspaceTitle(sessionId: string, title: string) {
  emit<WorkspaceTitleDetail>(BuilderSessionEvents.workspaceTitle, { sessionId, title });
}

export function onWorkspaceTitle(handler: (detail: WorkspaceTitleDetail) => void) {
  return subscribe<WorkspaceTitleDetail>(BuilderSessionEvents.workspaceTitle, handler);
}

/** Draft "Validate workflow" clicked — sticky lifecycle step → Validate. */
export function emitValidationStarted(sessionId?: string) {
  emit<SessionIdDetail>(BuilderSessionEvents.validationStarted, { sessionId });
}

export function onValidationStarted(handler: (detail: SessionIdDetail) => void) {
  return subscribe<SessionIdDetail>(BuilderSessionEvents.validationStarted, handler);
}
